import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  computed,
  inject,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { PermissionService } from '@app/core/services/permission.service';

import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ActionButton,
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  ONLINE_STORE_SEARCH_CONFIG,
  ONLINE_STORE_COLUMNS,
  ONLINE_STORE_DEFAULT_COLUMNS_ORDER,
  ONLINE_STORE_FLEXIBLE_COLUMNS_ORDER,
  ONLINE_STORE_ACTION_COLUMN,
  convertChannelTypeToLabel,
} from './online-store.config';
import {
  defaultOnlineStoreStatusSeverity,
  defaultOnlineStoreStatusStyle,
  isLazadaOnlineStore,
  lazadaOnlineStoreStatusSeverity,
  lazadaOnlineStoreStatusStyle,
} from '../utils/lazada-integration.util';
import { isShopeeOnlineStore } from '../utils/shopee-integration.util';

// Import API service directly
import { OnlineShopControllerService } from '@app/api/channel-service/api/onlineShopController.service';
import { ChannelIntegrationService } from '@app/api/channel-service/api/channelIntegration.service';
import { OnlineShopShowDTO } from '@app/api/channel-service/model/onlineShopShowDTO';
import { PageRequestDTO } from '@app/api/channel-service/model/pageRequestDTO';
import { ShopDeleteDTO } from '@app/api/channel-service/model/shopDeleteDTO';

// TypeScript interfaces for better type safety
interface OnlineStore {
  id: number;
  name: string;
  code: string;
  channelTypeCode: string;
  channelTypeMeaning: string;
  channelSubTypeCode?: string;
  onlineStoreTypeCode: string;
  onlineStoreTypeMeaning: string;
  manager: string;
  salesOrganizingId?: number;
  orgName: string;
  invoiceTypeCode: string;
  relationPhysicalShopId?: number;
  relationPhysicalShopName?: string;
  description?: string;
  url?: string;
  channelWarehouseName: string;
  channelWarehouseStatus: boolean;
  status: boolean;
  createdBy?: string;
  createdName?: string;
  creationDate: string;
  lastUpdatedBy?: string;
  lastUpdateDate?: string;
  objectVersionNumber: number;
  websitePlatform?: string;
  ecommercePlatform?: string;
  channelType?: string;
  channelPlatform?: string;
  connectionStatus?: 'CONNECTED' | 'NOT_CONNECTED' | 'TEMP_ERROR';
  virtualWarehouseNames?: string[] | null;
}

@Component({
  selector: 'app-online-store',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './online-store.component.html',
  styleUrls: ['./online-store.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnlineStoreComponent implements OnInit {
  // Search configuration
  searchConfig = ONLINE_STORE_SEARCH_CONFIG;

  // Table configuration (kept for reference, but using advanced table columns below)
  tableColumns = ONLINE_STORE_COLUMNS;

  // Button configuration base
  private readonly primaryButtonBase: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
  };

  private readonly secondaryButtonBase: ActionButton = {
    label: 'common.actions.delete',
    severity: 'danger',
    show: true,
    icon: 'delete',
  };

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'online_store' for channel permissions
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.online_store.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.online_store.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.online_store.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.online_store.approve');
  });
  canView = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.online_store.view');
  });

  // Computed button configurations with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  secondaryButton = computed(() => ({
    ...this.secondaryButtonBase,
    show: this.canDelete() && this.hasDeletableStores(),
  }));

  // Check if user has any action permissions
  hasAnyActionPermission = computed(
    () =>
      this.canUpdate() ||
      this.canDelete() ||
      this.canApprove() ||
      this.canView(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      ONLINE_STORE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of ONLINE_STORE_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(ONLINE_STORE_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      ONLINE_STORE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of ONLINE_STORE_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Data signals with proper typing
  onlineStores = signal<OnlineStore[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    name: '',
    channelTypeCode: '',
    onlineStoreTypeCode: '',
    status: undefined,
    orgName: '',
    page: 1,
    size: 20,
  });

  // Selection with proper typing
  selectedOnlineStores = signal<OnlineStore[]>([]);

  // Computed properties
  hasSelectedStores = computed(() => this.selectedOnlineStores().length > 0);
  hasDeletableStores = computed(() => {
    const selected = this.selectedOnlineStores();
    return selected.some((store) => !store.channelWarehouseStatus);
  });
  hasActiveStores = computed(() =>
    this.selectedOnlineStores().some((store) => store.channelWarehouseStatus),
  );

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  confirmationMessage = signal('');
  confirmationTitle = signal('');
  confirmationButtons = signal<any[]>([]);
  confirmationAction = signal('');
  confirmationData = signal<any>(null);

  constructor(
    private readonly onlineShopService: OnlineShopControllerService,
    private readonly channelIntegrationService: ChannelIntegrationService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly http: HttpClient,
  ) {}

  ngOnInit(): void {
    // Check for OAuth callback parameters
    const code = this.route.snapshot.queryParams['code'];
    const stateParam = this.route.snapshot.queryParams['state'];

    if (code && stateParam) {
      // Handle OAuth callback
      this.handleOAuthCallback(code, stateParam);
    } else {
      // Normal load
      this.loadOnlineStores();
    }
  }

  /**
   * Handle OAuth callback after connection
   */
  private handleOAuthCallback(code: string, stateParam: string): void {
    try {
      const url = this.router.url;
      const isShopee = url.includes('/online-store/shopee');
      const isLazada = url.includes('/online-store/lazada');

      let shopId = '';

      if (isShopee) {
        // Shopee state is just the raw shopId string
        shopId = stateParam;
      } else {
        // Lazada and other platforms use base64 encoded JSON state
        try {
          const decodedBase64 = atob(decodeURIComponent(stateParam));
          const state = JSON.parse(decodedBase64);
          shopId = state.shopId || '';

          if (isLazada) {
            const page = parseInt(state.page || '1', 10);
            const size = parseInt(state.size || '20', 10);
            this.searchParams.set({
              ...this.searchParams(),
              page: page,
              size: size,
            });
          }
        } catch (parseError) {
          console.error('Failed to parse OAuth state parameter', parseError);
          // Fallback to stateParam itself if parsing fails
          shopId = stateParam || '';
        }
      }

      // Show loading spinner
      this.loading.set(true);

      // Call callback API
      const callback$ = isShopee
        ? this.http.get(
            // ponytail: no generated client method for Shopee's dedicated callback
            // (it needs shop_id, the generic callback doesn't), so hand-rolled —
            // but must use the channel-service basePath, not a bare /v1 path that
            // falls through to the SPA and never reaches the backend.
            `${environment.channelServiceBasePath}/v1/channel-integration/shopee/oauth/callback`,
            {
              params: {
                code,
                shop_id: this.route.snapshot.queryParams['shop_id'],
                state: shopId,
              },
              responseType: 'text',
            },
          )
        : this.channelIntegrationService.callback(code, shopId);

      callback$.subscribe({
        next: () => {
          // Show success message
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'onlineStore.messages.connect_success',
            ),
          });

          // Remove query params from URL
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          });

          // Reload data with updated page and size
          this.loadOnlineStores();
        },
        error: (error) => {
          console.error('Error in OAuth callback:', error);
          // Hide loading spinner on error
          this.loading.set(false);

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'onlineStore.messages.connect_callback_error',
            ),
          });

          // Remove query params from URL even on error
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          });

          // Still reload data
          this.loadOnlineStores();
        },
      });
    } catch (error) {
      console.error('Error parsing state:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'onlineStore.messages.connect_state_parse_error',
        ),
      });

      // Remove query params and load normally
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
      this.loadOnlineStores();
    }
  }

  /**
   * Load online stores based on search parameters
   */
  loadOnlineStores(): void {
    this.loading.set(true);

    // Map search parameters to API format
    const pageRequest: PageRequestDTO = {
      page: this.searchParams().page - 1, // API uses 0-based indexing
      size: this.searchParams().size,
      sortBy: 'lastUpdateDate',
      ascending: false
    };

    const searchDTO: OnlineShopShowDTO = {
      name: this.searchParams().name || '',
      channelTypeCode: this.searchParams().channelTypeCode,
      onlineStoreTypeCode: this.searchParams().onlineStoreTypeCode || '',
      status: this.searchParams().status,
      orgName: this.searchParams().orgName || '',
      deleteFlag: false,
      pageRequest: pageRequest,
    };

    this.onlineShopService.showPage1(searchDTO, 'body', false).subscribe({
      next: (response: any) => {
        const applyResponse = (pageContent: OnlineStore[], totalElements: number) => {
          const mappedContent: OnlineStore[] = (pageContent || []).map((item) => {
            const virtualName =
              Array.isArray(item.virtualWarehouseNames) &&
              item.virtualWarehouseNames.length > 0
                ? item.virtualWarehouseNames[0]
                : '';

            const effectiveChannelWarehouseName =
              item.channelWarehouseName || virtualName || '';

            return {
              ...item,
              channelWarehouseName: effectiveChannelWarehouseName,
            } as OnlineStore;
          });

          this.onlineStores.set(mappedContent);
          this.totalRecords.set(totalElements);
        };

        // Handle blob response
        if (response instanceof Blob) {
          response
            .text()
            .then((text) => {
              try {
                const jsonData = JSON.parse(text);
                applyResponse(
                  (jsonData.pageContent || []) as OnlineStore[],
                  jsonData.totalElements || 0
                );
                this.loading.set(false);
              } catch (error) {
                console.error('Error parsing blob data:', error);
                this.loading.set(false);
              }
            })
            .catch((error) => {
              console.error('Error reading blob data:', error);
              this.loading.set(false);
            });
        } else {
          applyResponse(
            (response?.pageContent || []) as OnlineStore[],
            response?.totalElements || 0
          );
          this.loading.set(false);
        }
      },
      error: (error: any) => {
        console.error('Error loading online stores:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'onlineStore.messages.load_error',
          ),
        });
      },
    });
  }

  /**
   * Handle search form submission
   */
  onSearch(searchData: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      ...searchData,
      page: 1,
    });
    this.loadOnlineStores();
  }

  /**
   * Handle search form reset
   */
  onReset(): void {
    this.searchParams.set({
      name: '',
      channelTypeCode: '',
      onlineStoreTypeCode: '',
      status: undefined,
      orgName: '',
      page: 1,
      size: 20,
    });
    this.selectedOnlineStores.set([]);
    this.loadOnlineStores();

    // Show success message
    this.messageService.add({
      severity: 'info',
      summary: this.translateService.instant('common.messages.info'),
      detail: this.translateService.instant(
        'onlineStore.messages.search_reset',
      ),
    });
  }

  /**
   * Handle page change
   */
  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page + 1,
    });
    this.loadOnlineStores();
  }

  /**
   * Handle selection change
   */
  onSelectionChange(selection: OnlineStore[]): void {
    this.selectedOnlineStores.set(selection);
    // secondaryButton is a computed signal, so it will update automatically
  }

  /**
   * Navigate to add new store
   */
  onAdd(): void {
    this.router.navigate(['/channel/online-store/add']);
  }

  /**
   * Navigate to edit store
   */
  onEdit(store: OnlineStore): void {
    this.router.navigate(['/channel/online-store/edit', store.id]);
  }

  /**
   * Navigate to view store
   */
  onView(store: OnlineStore): void {
    this.router.navigate(['/channel/online-store/view', store.id]);
  }

  /**
   * Navigate to the product-publish form for a connected marketplace store.
   * Without this there is no UI entry to /channel/online-store/:id/product-listing/add.
   */
  onPublishProduct(store: OnlineStore): void {
    this.router.navigate(['/channel/online-store', store.id, 'product-listing', 'add']);
  }

  /**
   * Handle connect action
   */
  onConnect(store: OnlineStore): void {
    const shopId = Number(store.id);
    const pageNumber = this.searchParams().page.toString();
    const pageSize = this.searchParams().size.toString();
    // State contains page number, shopId, and page size - encode to base64
    const stateJson = JSON.stringify({
      page: pageNumber,
      shopId: shopId.toString(),
      size: pageSize,
    });
    const stateBase64 = btoa(stateJson);

    // Shopee generates its own CSRF state server-side (only shopId needed); Lazada passes state.
    const authUrl$ = isShopeeOnlineStore(store)
      ? this.channelIntegrationService.getShopeeAuthorizationUrl(shopId, 'response')
      : this.channelIntegrationService.getAuthorizationUrl(
          shopId,
          stateBase64,
          'response',
        );

    authUrl$
      .subscribe({
        next: (response: HttpResponse<string | Blob>) => {
          const responseBody = response.body;
          let authUrl: string = '';

          // Handle Blob response
          if (
            responseBody &&
            typeof responseBody !== 'string' &&
            responseBody instanceof Blob
          ) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                authUrl = reader.result as string;
                this.redirectToAuthUrl(authUrl, store);
              } catch (error) {
                console.error('Error reading blob:', error);
                this.showConnectError(store);
              }
            };
            reader.onerror = () => {
              console.error('Error reading blob');
              this.showConnectError(store);
            };
            reader.readAsText(responseBody);
          } else {
            // Handle string response
            authUrl = (responseBody as string) || '';
            this.redirectToAuthUrl(authUrl, store);
          }
        },
        error: (error) => {
          console.error('Error getting authorization URL:', error);
          this.showConnectError(store);
        },
      });
  }

  /**
   * Redirect to authorization URL
   */
  private redirectToAuthUrl(authUrl: string, store: OnlineStore): void {
    if (!authUrl || authUrl.trim() === '') {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'onlineStore.messages.connect_url_empty',
        ),
      });
      return;
    }

    // Ensure URL is absolute (has protocol)
    let finalUrl = authUrl.trim();
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      // If URL doesn't have protocol, it might be relative - use as is or prepend https://
      // But typically API should return full URL, so log warning
      console.warn('Authorization URL does not have protocol:', finalUrl);
    }

    // Redirect to the URL directly (opens in current tab)
    window.location.href = finalUrl;
  }

  /**
   * Show connect error message
   */
  private showConnectError(store: OnlineStore): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: this.translateService.instant(
        'onlineStore.messages.connect_error',
        {
          name: store.name,
        },
      ),
    });
  }

  /**
   * Handle status change
   */
  onStatusChange(store: OnlineStore, newStatus: boolean): void {
    const action = newStatus ? 'enable' : 'disable';
    const message = this.translateService.instant(
      `onlineStore.messages.confirm_${action}`,
      { name: store.name },
    );
    const title = this.translateService.instant(
      newStatus
        ? 'onlineStore.dialog.activate_title'
        : 'onlineStore.dialog.deactivate_title',
    );

    this.confirmationMessage.set(message);
    this.confirmationTitle.set(title);
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          newStatus
            ? 'onlineStore.actions.activate'
            : 'onlineStore.actions.deactivate',
        ),
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.confirmationAction.set('statusChange');
    this.confirmationData.set({ store, newStatus });
    this.showConfirmationDialog.set(true);
  }

  /**
   * Handle delete store
   */
  onDelete(store: OnlineStore): void {
    if (store.channelWarehouseStatus) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'onlineStore.messages.cannot_delete_enabled',
        ),
      });
      return;
    }

    this.confirmationMessage.set(
      this.translateService.instant('onlineStore.messages.confirm_delete', {
        name: store.name,
      }),
    );
    this.confirmationTitle.set(
      this.translateService.instant('onlineStore.dialog.delete_title'),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.confirmationAction.set('delete');
    this.confirmationData.set({ store });
    this.showConfirmationDialog.set(true);
  }

  /**
   * Handle batch delete
   */
  onBatchDelete(): void {
    const selectedStores = this.selectedOnlineStores();
    const activeStores = selectedStores.filter((store) => store.status);

    if (activeStores.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'onlineStore.messages.cannot_delete_active_batch',
        ),
      });
      return;
    }

    this.confirmationMessage.set(
      this.translateService.instant(
        'onlineStore.messages.confirm_batch_delete',
        {
          count: selectedStores.length,
        },
      ),
    );
    this.confirmationTitle.set(
      this.translateService.instant('onlineStore.dialog.batch_delete_title'),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.confirmationAction.set('batchDelete');
    this.confirmationData.set({ selectedStores });
    this.showConfirmationDialog.set(true);
  }

  /**
   * Handle confirmation dialog actions
   */
  onConfirmationDialogAction(action: string): void {
    this.showConfirmationDialog.set(false);

    if (action === 'confirm') {
      switch (this.confirmationAction()) {
        case 'statusChange':
          this.executeStatusChange(
            this.confirmationData().store,
            this.confirmationData().newStatus,
          );
          break;
        case 'delete':
          this.executeDelete(this.confirmationData().store);
          break;
        case 'batchDelete':
          this.executeBatchDelete(this.confirmationData().selectedStores);
          break;
      }
    }
  }

  /**
   * Execute status change
   */
  private executeStatusChange(store: OnlineStore, newStatus: boolean): void {
    const action = newStatus ? 'enable' : 'disable';
    const objectVersionNumber = store.objectVersionNumber || 0;
    this.onlineShopService
      .operation1(Number(store.id), newStatus, objectVersionNumber)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              `onlineStore.messages.${action}_success`,
            ),
          });
          this.loadOnlineStores();
        },
        error: (error) => {
          console.error(`Error ${action}ing store:`, error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              `onlineStore.messages.${action}_error`,
            ),
          });
        },
      });
  }

  /**
   * Execute delete
   */
  private executeDelete(store: OnlineStore): void {
    const objectVersionNumber = store.objectVersionNumber || 0;
    this.onlineShopService
      .delete1(Number(store.id), objectVersionNumber)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'onlineStore.messages.delete_success',
            ),
          });
          this.loadOnlineStores();
        },
        error: (error) => {
          console.error('Error deleting store:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'onlineStore.messages.delete_error',
            ),
          });
        },
      });
  }

  /**
   * Execute batch delete
   */
  private executeBatchDelete(selectedStores: OnlineStore[]): void {
    const deleteDTO: ShopDeleteDTO = {
      ids: selectedStores.map((store) => Number(store.id)),
    };

    this.onlineShopService.deletes1(deleteDTO).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'onlineStore.messages.batch_delete_success',
          ),
        });
        this.selectedOnlineStores.set([]);
        this.loadOnlineStores();
      },
      error: (error) => {
        console.error('Error batch deleting stores:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'onlineStore.messages.batch_delete_error',
          ),
        });
      },
    });
  }

  /**
   * Get status icon class
   */
  getStatusIcon(status: boolean): string {
    if (status) {
      return 'pi pi-check-circle';
    }
    return 'pi pi-times-circle';
  }

  /**
   * Returns true when the row is a Lazada channel.
   * Lazada channels show the Connect button and "Not connected" status when applicable.
   */
  isLazadaChannel(rowData: OnlineStore | undefined): boolean {
    return isLazadaOnlineStore(rowData);
  }

  /** Returns true when the row is a Shopee channel. */
  isShopeeChannel(rowData: OnlineStore | undefined): boolean {
    return isShopeeOnlineStore(rowData);
  }

  /**
   * Channels that use the OAuth Connect button and connection-aware status
   * (Lazada and Shopee). Other channels show active/inactive only.
   */
  isConnectableChannel(rowData: OnlineStore | undefined): boolean {
    return this.isLazadaChannel(rowData) || this.isShopeeChannel(rowData);
  }

  /**
   * Status label for the table. Lazada: active / not connected / inactive by connectionStatus.
   * Other channels: active or inactive only (connectionStatus ignored).
   */
  getStatusDisplayText(
    status: boolean,
    connectionStatus?: 'CONNECTED' | 'NOT_CONNECTED' | 'TEMP_ERROR',
    rowData?: OnlineStore,
  ): string {
    if (!this.isConnectableChannel(rowData)) {
      return status
        ? this.translateService.instant('common.table.status.active')
        : this.translateService.instant('common.table.status.inactive');
    }
    if (status && connectionStatus === 'CONNECTED') {
      return this.translateService.instant('common.table.status.active');
    }
    if (status && (connectionStatus === 'NOT_CONNECTED' || !connectionStatus)) {
      return this.translateService.instant(
        'onlineStore.table.status.not_connected',
      );
    }
    return this.translateService.instant('common.table.status.inactive');
  }

  /**
   * Tag severity for status. Lazada uses warning for not connected; others use success/danger only.
   */
  getStatusSeverity(
    status: boolean,
    connectionStatus?: 'CONNECTED' | 'NOT_CONNECTED' | 'TEMP_ERROR',
    rowData?: OnlineStore,
  ): string {
    if (!this.isConnectableChannel(rowData)) {
      return defaultOnlineStoreStatusSeverity(status);
    }
    return lazadaOnlineStoreStatusSeverity(status, connectionStatus);
  }

  /**
   * Inline style for status tag. Lazada: not-connected uses warning colors; others use active/inactive only.
   */
  getStatusStyle(
    status: boolean,
    connectionStatus?: 'CONNECTED' | 'NOT_CONNECTED' | 'TEMP_ERROR',
    rowData?: OnlineStore,
  ): Record<string, string> {
    if (!this.isConnectableChannel(rowData)) {
      return defaultOnlineStoreStatusStyle(status);
    }
    return lazadaOnlineStoreStatusStyle(status, connectionStatus);
  }

  /**
   * Get connection status display text
   */
  getConnectionStatusDisplayText(
    connectionStatus?: 'CONNECTED' | 'NOT_CONNECTED' | 'TEMP_ERROR',
  ): string {
    if (!connectionStatus) {
      return this.translateService.instant(
        'onlineStore.table.connection_status.not_connected',
      );
    }
    return this.translateService.instant(
      `onlineStore.table.connection_status.${connectionStatus.toLowerCase()}`,
    );
  }

  /**
   * Get connection status severity
   */
  getConnectionStatusSeverity(
    connectionStatus?: 'CONNECTED' | 'NOT_CONNECTED' | 'TEMP_ERROR',
  ): string {
    if (!connectionStatus) {
      return 'danger';
    }
    switch (connectionStatus) {
      case 'CONNECTED':
        return 'success';
      case 'NOT_CONNECTED':
        return 'danger';
      case 'TEMP_ERROR':
        return 'warning';
      default:
        return 'danger';
    }
  }

  /**
   * Get connection status style
   */
  getConnectionStatusStyle(
    connectionStatus?: 'CONNECTED' | 'NOT_CONNECTED' | 'TEMP_ERROR',
  ): any {
    if (!connectionStatus) {
      return {
        '--status-color': '#ef4444',
      };
    }
    switch (connectionStatus) {
      case 'CONNECTED':
        return {
          '--status-color': '#22c55e',
        };
      case 'NOT_CONNECTED':
        return {
          '--status-color': '#ef4444',
        };
      case 'TEMP_ERROR':
        return {
          '--status-color': '#f59e0b',
        };
      default:
        return {
          '--status-color': '#ef4444',
        };
    }
  }

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;

  /**
   * Convert channel type code to display label
   */
  getChannelTypeLabel(code: string | null | undefined): string {
    if (!code) {
      return '';
    }
  
    const label = convertChannelTypeToLabel(code);
    if (!label) {
      return code;
    }
  
    const translated = this.translateService.instant(label);
    return translated === label ? code : translated;
  }

  /**
   * Convert online store type code to display label using dynamic i18n key
   */
  getOnlineStoreTypeLabel(code: string): string {
    if (!code) {
      return '';
    }
    const i18nKey = `onlineStore.store_types.${code.toLowerCase()}`;
    const translated = this.translateService.instant(i18nKey);

    // If translation key not found, return the original value
    if (translated === i18nKey) {
      return code;
    }
    return translated;
  }

  /**
   * Get platform label using dynamic i18n key
   */
  getPlatformLabel(value: string): string {
    if (!value) return '';
    const i18nKey = `onlineStore.channel_sub_types.${value.toLowerCase()}`;
    const translated = this.translateService.instant(i18nKey);

    // If translation key not found, return the original value
    if (translated === i18nKey) {
      return value;
    }
    return translated;
  }

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
